/**
 * Shared non-OK response handling for the raw-`fetch` service layer.
 *
 * Services built on the axios `api` instance get status checking, user-facing message mapping
 * and error toasts for free from its response interceptor (see api.ts). Services built on
 * `fetchWithAuth` get none of that: `fetch` resolves normally on 4xx/5xx, so a missing
 * `if (!res.ok)` turns a 403 into an empty list, or a silent "success". These helpers close
 * that gap and route the body through the SAME parseApiError policy the axios path uses, so
 * both layers phrase the same failure identically.
 *
 * Note parseApiError deliberately masks raw 401/403 bodies (they carry role lists and
 * internals) — do not bypass it to surface the backend string verbatim.
 */

import type { AxiosError } from 'axios';
import { parseApiError } from '../utils/errorParser';
import { ErrorDomain, ErrorType, type ParsedError } from '../types/errors';
import { logger } from './logger';

/**
 * Error thrown for a non-OK HTTP response. `message` is always safe to show to a user;
 * `status`/`parsed` are there for callers that need to branch (e.g. treat 403 as "hide the
 * panel" rather than "show a toast").
 */
export class HttpError extends Error {
  readonly status: number;
  readonly parsed: ParsedError;

  constructor(message: string, status: number, parsed: ParsedError) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.parsed = parsed;
    // Required for `instanceof HttpError` to survive the ES5 downlevel target.
    Object.setPrototypeOf(this, HttpError.prototype);
  }

  get isPermissionDenied(): boolean {
    return this.status === 403 || this.parsed.type === ErrorType.PERMISSION_DENIED;
  }
}

export function isHttpError(err: unknown): err is HttpError {
  return err instanceof HttpError;
}

/** Outcome of reading a body: `parseable` is false only for a non-empty, non-JSON payload. */
interface ParsedBody {
  parseable: boolean;
  value: unknown;
}

/**
 * Reads a response body without ever throwing. `parseable: false` means the body held
 * something that is not JSON — a 502/504 from the edge is an HTML page, and calling res.json()
 * on it throws a SyntaxError whose raw text ("Unexpected token '<'...") used to reach users
 * as a toast.
 *
 * Prefers text() so that non-JSON can be detected rather than thrown on. Falls back to json()
 * for Response-like test doubles, which commonly implement only { ok, json }.
 */
async function readBody(res: Response): Promise<ParsedBody> {
  if (typeof (res as { text?: unknown }).text === 'function') {
    const text = await res.text().catch(() => '');
    if (!text.trim()) return { parseable: true, value: null };
    try {
      return { parseable: true, value: JSON.parse(text) };
    } catch {
      return { parseable: false, value: null };
    }
  }

  if (typeof (res as { json?: unknown }).json === 'function') {
    try {
      return { parseable: true, value: await res.json() };
    } catch {
      return { parseable: false, value: null };
    }
  }

  return { parseable: true, value: null };
}

// Used when the response carried no usable JSON body, so parseApiError has nothing to work
// with. Mirrors the phrasing parseApiError itself produces for these statuses.
function messageForStatus(status: number, fallback: string): string {
  if (status === 401) return 'Sessão expirada. Por favor, faça login novamente.';
  if (status === 403) return 'Você não tem permissão para realizar esta ação.';
  if (status === 404) return 'Recurso não encontrado.';
  if (status === 502 || status === 503 || status === 504) {
    return 'Serviço temporariamente indisponível. Tente novamente em instantes.';
  }
  return fallback;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Builds the HttpError for an already-known-bad response. Consumes the body, so call this at
 * most once per response.
 */
export async function toHttpError(res: Response, fallbackMessage: string): Promise<HttpError> {
  const { value: body } = await readBody(res);

  // parseApiError recognizes a structured body by `code` and a legacy one by `error`; a body
  // carrying only `message` would otherwise fall through to "Erro desconhecido". Alias it so
  // the backend's own wording is used (parseLegacyError prefers `message` over `error`).
  const data = isRecord(body) && !('code' in body) && !('error' in body) && 'message' in body
    ? { ...body, error: String(body.message ?? '') }
    : body;

  // parseApiError only ever reads error.response?.status and error.response?.data, so this
  // minimal shape is sufficient — no real AxiosError needed.
  const parsed = parseApiError({ response: { status: res.status, data } } as AxiosError);

  const message = parsed.code === 'UNKNOWN_ERROR'
    ? messageForStatus(res.status, fallbackMessage)
    : parsed.message || messageForStatus(res.status, fallbackMessage);

  return new HttpError(message, res.status, parsed);
}

/**
 * Throws an HttpError when the response is not OK. For endpoints whose body is irrelevant
 * (DELETE, fire-and-forget commands) — without this, a failed call is indistinguishable from
 * a successful one and the UI reports success.
 */
export async function ensureOk(res: Response, fallbackMessage: string): Promise<void> {
  if (!res.ok) {
    throw await toHttpError(res, fallbackMessage);
  }
}

/**
 * For deliberately fire-and-forget requests: cloud-side bookkeeping that must not interrupt the
 * operation that triggered it. The main case is the device sync/revoke ledger — the device
 * operation has already happened, so failing the user's action over a bookkeeping PATCH would be
 * wrong, but a bare `.catch(() => {})` made a rejected write invisible and let the ledger drift
 * silently (which then drives redundant re-syncs and re-revokes).
 *
 * Never rejects. Checks the status and logs on failure, so the drift is at least diagnosable.
 */
export function fireAndForget(request: Promise<Response>, context: string): void {
  void request
    .then(async res => {
      if (!res.ok) {
        throw await toHttpError(res, 'requisição rejeitada');
      }
    })
    .catch((err: unknown) => {
      logger.warn('httpError.fireAndForget', `${context} failed`, {
        error: err instanceof Error ? err.message : String(err),
        status: err instanceof HttpError ? err.status : undefined,
      });
    });
}

/**
 * Throws on a non-OK response, otherwise parses the body as JSON.
 *
 * Returns `{}` (cast to T) for a successful response with an empty body — 204 No Content and
 * the several endpoints here that reply with no payload — so existing callers doing
 * `(await okJson(...)).items || []` keep working instead of hitting "cannot read property of
 * undefined".
 */
export async function okJson<T>(res: Response, fallbackMessage: string): Promise<T> {
  if (!res.ok) {
    throw await toHttpError(res, fallbackMessage);
  }
  const { parseable, value } = await readBody(res);
  if (!parseable) {
    // 200 with a non-JSON body means something in front of the API is returning HTML.
    throw new HttpError(fallbackMessage, res.status, {
      type: ErrorType.SERVER_ERROR,
      domain: ErrorDomain.UNKNOWN,
      code: 'INVALID_JSON',
      message: fallbackMessage,
      httpStatus: res.status,
      isRetryable: true,
    });
  }
  if (value === null || value === undefined) return {} as T;
  return value as T;
}
